import React from "react";
import { Text, Flex } from "@jsq/ds/core";
import { DetailedBecsPaymentPrefObjectType } from "../../../common/types";
import FormattedAddress from "../FormattedAddress";
import PaymentDetailsModalField from "../PaymentDetailsModalField";
import { BecsVisibleFields } from "../types";
import { getAddress } from "../utils";
import { isBecsFieldVisible } from "../utils/fieldVisibility";

export type BecsPaymentPrefDisplayProps = {
  becsPaymentPrefFull: DetailedBecsPaymentPrefObjectType;
  visibleFields?: BecsVisibleFields;
};

/**
 * Formats a 6-digit BSB code with a dash (e.g., '123456' -> '123-456').
 */
const formatBsbCode = (bsbCode: string | null): string | null => {
  if (!bsbCode) return null;
  const digits = bsbCode.replace(/\D/g, "");
  if (digits.length !== 6) return bsbCode;
  return `${digits.slice(0, 3)}-${digits.slice(3)}`;
};

export const BecsPaymentPrefDisplay: React.FC<BecsPaymentPrefDisplayProps> = ({
  becsPaymentPrefFull,
  visibleFields,
}: BecsPaymentPrefDisplayProps) => {
  const accountHolderAddress =
    becsPaymentPrefFull.accountHolderAddress &&
    getAddress(becsPaymentPrefFull.accountHolderAddress);

  const gap = 8; // gap between rows and columns

  return (
    <Flex gap={gap} flexDirection="column" data-testid="BecsPaymentPrefDisplayFlex">
      <PaymentDetailsModalField
        label="Account holder name"
        value={becsPaymentPrefFull.accountName}
        gap={gap}
        visible={isBecsFieldVisible("accountName", visibleFields)}
      />
      {isBecsFieldVisible("accountHolderAddress", visibleFields) && accountHolderAddress && (
        <Flex gap={gap}>
          <Flex flexBasis="60%">
            <Text variant="secondary-md">Address</Text>
          </Flex>
          <Flex flexBasis="100%" flexDirection="column">
            <FormattedAddress address={accountHolderAddress} variant="regular-md" />
          </Flex>
        </Flex>
      )}
      <PaymentDetailsModalField
        label="BSB code"
        value={formatBsbCode(becsPaymentPrefFull.bank?.bsb ?? null)}
        gap={gap}
        visible={isBecsFieldVisible("bank", visibleFields)}
      />
      <PaymentDetailsModalField
        label="Account number"
        value={becsPaymentPrefFull.accountNumber}
        gap={gap}
        visible={isBecsFieldVisible("accountNumber", visibleFields)}
      />
      {becsPaymentPrefFull.additionalInstructions && (
        <PaymentDetailsModalField
          label="Additional instructions"
          value={becsPaymentPrefFull.additionalInstructions}
          gap={gap}
          visible={isBecsFieldVisible("additionalInstructions", visibleFields)}
        />
      )}
    </Flex>
  );
};
